import { MONITOR_ARTIFACT_TYPES } from "../types/artifact";
import type { Artifact, InstagramPost } from "../types/artifact";
import type { ProcessSetupSnapshot } from "../types/state";

import { deriveProcessPlan } from "./actionBindings";

export type SetupCardItemKind =
  | "artifact"
  | "action"
  | "recipients"
  | "schedule";

export type SetupCardItem = {
  kind: SetupCardItemKind;
  id: string;
};

export const SCHEDULABLE_ARTIFACT_TYPES = new Set([
  "instagram_post",
  "email_html",
  "email_plaintext",
]);

// Artifact types that need no date and no funnel step of their own — they run
// the moment the campaign activates. A monitor beside one of these still has
// nothing to schedule, so isMonitorOnlyProcess treats the two the same way.
export const ALWAYS_ON_ARTIFACT_TYPES = new Set(["digital_ad"]);

// Canonical funnel stages, in display order. email_html and email_plaintext
// deliberately share rank 3 — they are one funnel stage ("email"), and the
// setup card numbers them as a single combined "Email N of M" group, so a
// campaign that mixes both subtypes must keep them contiguous and in the order
// they were submitted (preserved by the index tiebreak in sortSetupChildren).
export const CANONICAL_SETUP_CHILD_RANK: Readonly<Record<string, number>> = {
  subreddit_monitor: 0, // reddit monitor
  custom_monitor: 0, // generic monitor (same funnel stage as reddit monitor)
  instagram_post: 1, // IG carousel / post
  ig_comment_watcher_class: 2, // comment watcher
  email_html: 3, // email
  email_plaintext: 3, // email (same stage)
  digital_ad: 4, // ad
};

// Types not in the funnel map (e.g. instagram_dm, deferred_content) sort to the
// end, after every ranked stage.
const UNRANKED_SETUP_CHILD_RANK =
  Math.max(...Object.values(CANONICAL_SETUP_CHILD_RANK)) + 1;

function setupChildRank(type: string): number {
  return CANONICAL_SETUP_CHILD_RANK[type] ?? UNRANKED_SETUP_CHILD_RANK;
}

// Orders setup-card children into the fixed funnel sequence (reddit → IG post →
// comment watcher → email → ad → unranked). The original submission order
// arrives arbitrarily because the agent submits artifacts in no fixed order, so
// the card would otherwise render its steps shuffled. The sort is stable via an
// explicit index tiebreak: within a single stage (e.g. three emails, or a
// carousel's posts) the submission order is kept, which keeps the card's
// "N of M" numbering intuitive.
export function sortSetupChildren<T extends { type: string }>(
  children: readonly T[],
): T[] {
  return children
    .map((child, index) => ({ child, index }))
    .sort((a, b) => {
      const rankDelta =
        setupChildRank(a.child.type) - setupChildRank(b.child.type);
      return rankDelta !== 0 ? rankDelta : a.index - b.index;
    })
    .map((entry) => entry.child);
}

export function isSkipped(snapshot: ProcessSetupSnapshot, id: string): boolean {
  return (snapshot.skipped_artifact_ids ?? []).includes(id);
}

// Whether a child has at least one funnel sibling, by the RAW count — a
// skipped sibling still counts. This is the single source of truth for the
// "Approve" (funnel sibling) vs. direct-start (standalone) fork every monitor
// panel renders (CustomMonitorPanel, SubredditMonitorPanel,
// CommentWatcherArtifactPanel), and for isMonitorOnlyProcess's own guard below
// — so the two can never drift apart on where that boundary sits.
export function hasFunnelSibling(
  snapshot: Pick<ProcessSetupSnapshot, "children"> | null | undefined,
): boolean {
  return (snapshot?.children.length ?? 1) > 1;
}

// Activatable artifacts are the artifact-kind setup rows (publishing pieces plus
// monitors), so this is what the "at least one item must remain" guard counts.
// Takes the already-built item list rather than rebuilding it, so a caller that
// already called setupCardItems (as CampaignSetupPanel does) doesn't pay for
// the whole pipeline — deriveProcessPlan included — a second time.
export function nonSkippedActivatableArtifactCount(
  snapshot: ProcessSetupSnapshot,
  items: SetupCardItem[] = setupCardItems(snapshot),
): number {
  return items.filter(
    (item) => item.kind === "artifact" && !isSkipped(snapshot, item.id),
  ).length;
}

// All skip-aware derivations below flow through a single deriveProcessPlan call so
// the setup card, the schedule/recipients gating, the min-1 guard, and
// useGoToNextStep can never disagree about which actions survive a skip.

// The Schedule step appears when a surviving artifact is schedulable OR a surviving
// email-send action still has an email to send. Both are read from the plan, not
// raw actions/children, so skipping the only email also drops the Schedule row.
function hasSchedulableWork(
  snapshot: ProcessSetupSnapshot,
  survivingActions: ProcessSetupSnapshot["actions"],
): boolean {
  if (
    snapshot.children.some(
      (c) =>
        SCHEDULABLE_ARTIFACT_TYPES.has(c.type) && !isSkipped(snapshot, c.id),
    )
  ) {
    return true;
  }
  return survivingActions.some((a) => a.type === "send_email_to_contacts");
}

// A campaign whose work is entirely the owner's own. Bridgeway sends nothing and
// publishes nothing, so there is no date to pick — but the campaign still has to
// start, and the server activates it (it counts surviving owner asks). Without a
// final step the owner accepts every ask and the campaign can never begin.
export function isOwnerOnlyProcess(snapshot: ProcessSetupSnapshot): boolean {
  // `deriveProcessPlan` drops actions whose artifact was skipped. It does NOT
  // read skipped_action_ids, so the owner's own skips are removed here. Without
  // this a campaign whose every ask was skipped still read as owner work.
  const skippedActions = new Set(snapshot.skipped_action_ids ?? []);
  const survivingActions = deriveProcessPlan(snapshot).survivingActions.filter(
    (a) => !skippedActions.has(a.id),
  );
  if (survivingActions.length === 0) return false;
  // Every surviving action must be an owner ask. Tested this way round on
  // purpose: a list of "things that mean Bridgeway acts" would silently admit
  // each new action type, and the campaign would promise to send nothing while
  // publishing a reply or a DM.
  if (survivingActions.some((a) => a.type !== "request_owner_action")) {
    return false;
  }
  // An artifact is content Bridgeway produces, so a campaign carrying one is
  // not the owner's alone. An ask holds its own instruction and needs none.
  return !snapshot.children.some((c) => !isSkipped(snapshot, c.id));
}

// A campaign whose only contents are monitors, optionally beside an always-on
// artifact (e.g. a digital_ad) that likewise needs no date. A monitor holds its
// own runtime (a cadence, or the Instagram webhook), so there is no date to
// pick — but the campaign still has to start. Without a final step the owner
// approves every monitor and the campaign can never begin.
// A campaign whose surviving contents are entirely always-on artifacts, with no
// monitor among them. `digital_ad_content.md` tells the agent that two or more
// ads are ONE campaign, proposed as sibling `digital_ad` artifacts in a single
// `propose_process` call, so "three product ads" lands exactly here. Nothing is
// schedulable, no action sends an email, and there is no monitor, so without
// this the campaign held nothing that could start it.
//
// There is deliberately NO raw-child-count guard, which is the one way this
// differs from isMonitorOnlyProcess. A sole monitor renders its own direct
// start control, so a campaign-level row beside it would sit inert forever. An
// always-on artifact renders no such control: DigitalAdArtifactPanel offers
// Save, and classifyProcessPanel routes an ad-only process to the campaign
// dashboard. A sole ad therefore needs this row as much as three do.
export function isAlwaysOnOnlyProcess(snapshot: ProcessSetupSnapshot): boolean {
  const surviving = snapshot.children.filter((c) => !isSkipped(snapshot, c.id));
  return (
    surviving.length > 0 &&
    surviving.every((c) => ALWAYS_ON_ARTIFACT_TYPES.has(c.type))
  );
}

export function isMonitorOnlyProcess(snapshot: ProcessSetupSnapshot): boolean {
  // The RAW child count (hasFunnelSibling), deliberately not the surviving
  // list. All three monitor panels fork on this same raw count, which counts a
  // skipped child too: ui/src/spaces/chat/panels/CustomMonitorPanel.tsx,
  // ui/src/spaces/chat/panels/SubredditMonitorPanel.tsx, and
  // ui/src/spaces/chat/panels/CommentWatcherArtifactPanel.tsx. On a raw count of
  // 1 a monitor renders its own direct start control instead of "Approve", so it
  // never marks its setup item complete. A row here would then stay inert
  // forever and dead-end a campaign that starts correctly today. Counting only
  // the survivors instead would split the two: the panel would show "Approve"
  // and complete the item, and no row would exist to start the campaign.
  if (!hasFunnelSibling(snapshot)) return false;
  const surviving = snapshot.children.filter((c) => !isSkipped(snapshot, c.id));
  // At least one monitor must survive — an always-on artifact alone (no
  // monitor) is not "monitor-only", it's just nothing to schedule, which
  // hasSchedulableWork/isOwnerOnlyProcess already decide correctly.
  return (
    surviving.some((c) => MONITOR_ARTIFACT_TYPES.has(c.type)) &&
    surviving.every(
      (c) =>
        MONITOR_ARTIFACT_TYPES.has(c.type) ||
        ALWAYS_ON_ARTIFACT_TYPES.has(c.type),
    )
  );
}

// A process whose only sendable piece is a single email. These get a direct
// "Send now" path (email review -> recipients -> schedule) so a customer
// sending one email doesn't have to hunt for the buried send control. A
// multi-piece campaign is excluded: each of its pieces goes out at campaign
// activation, not one at a time.
export function isSingleSendableEmailProcess(
  snapshot: ProcessSetupSnapshot,
): boolean {
  const sendable = snapshot.children.filter((c) =>
    SCHEDULABLE_ARTIFACT_TYPES.has(c.type),
  );
  return (
    sendable.length === 1 &&
    (sendable[0].type === "email_html" ||
      sendable[0].type === "email_plaintext")
  );
}

export function setupCardItems(
  snapshot: ProcessSetupSnapshot,
): SetupCardItem[] {
  const items: SetupCardItem[] = [];

  // Artifact and action ROWS derive from the raw actions: a skipped artifact keeps
  // its row (rendered as "Skipped" with an Undo button) because skipping only adds
  // to skipped_artifact_ids — it never removes the action from the snapshot. Gating
  // rows on surviving actions would strip the skipped row's Undo affordance.
  const primaryArtifactIds = new Set<string>();

  for (const action of snapshot.actions) {
    const config = action.config as Record<string, unknown> | null;
    const artifactRef = config?.artifact_ref;
    if (typeof artifactRef === "string" && artifactRef) {
      const matched = snapshot.children.find(
        (c) =>
          c.id === artifactRef || (c as { ref?: string }).ref === artifactRef,
      );
      if (matched) primaryArtifactIds.add(matched.id);
    }
  }

  for (const artifact of snapshot.children) {
    if (
      primaryArtifactIds.has(artifact.id) ||
      ALWAYS_ON_ARTIFACT_TYPES.has(artifact.type) ||
      MONITOR_ARTIFACT_TYPES.has(artifact.type)
    ) {
      items.push({ kind: "artifact", id: artifact.id });
    }
  }

  for (const action of snapshot.actions) {
    const config = action.config as Record<string, unknown> | null;
    if (!config?.artifact_ref) {
      items.push({ kind: "action", id: action.id });
    }
  }

  // Conditional steps derive from SURVIVING actions: skipping the only email drops
  // its send action from the plan, so Recipients/Schedule disappear too. This is
  // the ADO-1169 v2 fix — these steps no longer key off raw actions.
  const { survivingActions } = deriveProcessPlan(snapshot);

  const emailSends = survivingActions.filter(
    (a) => a.type === "send_email_to_contacts",
  );
  if (emailSends.length > 0) {
    items.push({ kind: "recipients", id: `recipients:${snapshot.process_id}` });
  }

  // The last row is always the one that starts the campaign. With content it is
  // "Schedule and Activate", because a date has to be picked first. With only the
  // owner's own steps, or with only monitors, there is nothing to schedule, so
  // the row is a plain review that ends in start. All three resolve to the same
  // `schedule` step.
  if (
    hasSchedulableWork(snapshot, survivingActions) ||
    isOwnerOnlyProcess(snapshot) ||
    isMonitorOnlyProcess(snapshot) ||
    isAlwaysOnOnlyProcess(snapshot)
  ) {
    items.push({ kind: "schedule", id: `schedule:${snapshot.process_id}` });
  }

  return items;
}

// The steps still standing between the campaign and its schedule, in the order
// the card renders them. The gate below is defined as "this list is empty", so
// the row that names what's left and the rule that keeps it inert can never
// disagree about which steps count.
export function outstandingSetupItems(
  snapshot: ProcessSetupSnapshot,
): SetupCardItem[] {
  const items = setupCardItems(snapshot);
  const completed = new Set(snapshot.completed_item_ids ?? []);
  const skippedActions = new Set(snapshot.skipped_action_ids ?? []);
  const outstanding: SetupCardItem[] = [];

  for (const item of items) {
    if (item.kind === "artifact" && isSkipped(snapshot, item.id)) continue;
    // An owner ask is an action, and declining one is a real answer — without
    // this it could never be skipped and would block go-live forever.
    if (item.kind === "action" && skippedActions.has(item.id)) continue;
    if (item.kind === "artifact" || item.kind === "action") {
      if (!completed.has(item.id)) outstanding.push(item);
      continue;
    }
    if (item.kind === "recipients" && !snapshot.recipients_finalized) {
      outstanding.push(item);
    }
  }

  return outstanding;
}

export function isProcessReadyToSchedule(
  snapshot: ProcessSetupSnapshot,
): boolean {
  return outstandingSetupItems(snapshot).length === 0;
}

function isArtifactScheduled(artifact: Artifact): boolean {
  if (artifact.type === "instagram_post") {
    return (artifact as InstagramPost).schedule != null;
  }
  return true;
}

function isEmailSendScheduled(
  action: ProcessSetupSnapshot["actions"][number],
): boolean {
  if (action.type !== "send_email_to_contacts") return true;
  return action.trigger != null;
}

export function isProcessReadyToActivate(
  snapshot: ProcessSetupSnapshot,
): boolean {
  if (!isProcessReadyToSchedule(snapshot)) return false;

  for (const child of snapshot.children) {
    if (isSkipped(snapshot, child.id)) continue;
    if (
      SCHEDULABLE_ARTIFACT_TYPES.has(child.type) &&
      !isArtifactScheduled(child)
    ) {
      return false;
    }
  }

  for (const action of snapshot.actions) {
    if (!isEmailSendScheduled(action)) return false;
  }

  return true;
}
